import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Container, Grid, Stack, SwipeableDrawer, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { dispatch, useSelector } from '../../../redux/store';
import { PATH_DASHBOARD } from '../../../routes/paths';
import useSettings from '../../../hooks/useSettings';
import Page from '../../../components/Page';
import Iconify from '../../../components/Iconify';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../components/table/DataGridTable';
import { FormProvider, RHFSelect, RHFTextField } from '../../../components/hook-form';
import { addJobRole, getJobRoles, updateJobRole } from '../../../redux/slices/recruitmentRedux/jobRolesRedux';
import { getDepartments } from '../../../redux/slices/settingsManagementRedux/departmentsRedux';
import { accessVerify } from '../../../utils/common';
import PermissionRequired from '../../errorPages/permissionRequired';

const EMPLOYMENT_TYPES = [
  { value: 'full_time', label: 'Full Time' },
  { value: 'part_time', label: 'Part Time' },
  { value: 'contract', label: 'Contract' },
  { value: 'internship', label: 'Internship' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const dataModel = {
  id: null,
  name: '',
  code: '',
  departmentId: '',
  summary: '',
  responsibilities: '',
  requirements: '',
  employmentType: 'full_time',
  salaryMin: '',
  salaryMax: '',
  status: 'active',
};

export default function JobRoles() {
  const { themeStretch } = useSettings();
  const [selectedObject, setSelectedObject] = useState(dataModel);
  const [isAdd, setIsAdd] = useState(false);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState(false);

  const { data, addData } = useSelector((state) => state.recruitmentJobRoles);
  const { data: departmentData } = useSelector((state) => state.department);

  useEffect(() => {
    dispatch(getJobRoles());
    dispatch(getDepartments());
  }, []);

  const JobRoleSchema = Yup.object().shape({
    name: Yup.string().required('Job role name is required'),
    code: Yup.string().nullable(),
    departmentId: Yup.string().nullable(),
    summary: Yup.string().nullable(),
    responsibilities: Yup.string().nullable(),
    requirements: Yup.string().nullable(),
    employmentType: Yup.string().required('Employment type is required'),
    salaryMin: Yup.number()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .min(0, 'Minimum salary cannot be negative'),
    salaryMax: Yup.number()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .min(0, 'Maximum salary cannot be negative')
      .when('salaryMin', (salaryMin, schema) => (salaryMin ? schema.min(salaryMin, 'Maximum salary should be greater than minimum salary') : schema)),
    status: Yup.string().required('Status is required'),
  });

  const defaultValues = useMemo(
    () => ({
      name: selectedObject?.name || '',
      code: selectedObject?.code || '',
      departmentId: selectedObject?.departmentId || '',
      summary: selectedObject?.summary || '',
      responsibilities: selectedObject?.responsibilities || '',
      requirements: selectedObject?.requirements || '',
      employmentType: selectedObject?.employmentType || 'full_time',
      salaryMin: selectedObject?.salaryMin || '',
      salaryMax: selectedObject?.salaryMax || '',
      status: selectedObject?.status || 'active',
    }),
    [selectedObject]
  );

  const methods = useForm({
    resolver: yupResolver(JobRoleSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (addData?.data) {
      dispatch(getJobRoles());
      setIsModelOpen(false);
    }
  }, [addData]);

  const manageModel = (modelData, type) => {
    setIsModelOpen(true);
    setIsAdd(type === 'add');
    setSelectedObject(type === 'add' ? dataModel : modelData);
  };

  const onSubmit = async (value) => {
    const payload = {
      name: value?.name || '',
      code: value?.code || null,
      departmentId: value?.departmentId || null,
      summary: value?.summary || '',
      responsibilities: value?.responsibilities || '',
      requirements: value?.requirements || '',
      employmentType: value?.employmentType || 'full_time',
      salaryMin: value?.salaryMin === '' ? null : value?.salaryMin,
      salaryMax: value?.salaryMax === '' ? null : value?.salaryMax,
      status: value?.status || 'active',
    };

    if (isAdd) {
      dispatch(addJobRole(payload));
    } else {
      dispatch(updateJobRole({ ...payload, id: selectedObject?.id }));
    }
  };

  const columns = [
    {
      accessorKey: 'name',
      header: 'Job Role',
    },
    {
      accessorKey: 'code',
      header: 'Code',
    },
    {
      accessorKey: 'departmentName',
      header: 'Department',
    },
    {
      accessorKey: 'employmentType',
      header: 'Employment Type',
      Cell: ({ cell }) => EMPLOYMENT_TYPES.find((item) => item.value === cell.getValue())?.label || cell.getValue(),
    },
    {
      accessorKey: 'salaryMin',
      header: 'Min Salary',
    },
    {
      accessorKey: 'salaryMax',
      header: 'Max Salary',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      Cell: ({ cell }) => <Chip size='small' color={cell.getValue() === 'active' ? 'success' : 'default'} label={cell.getValue()} />,
    },
  ];

  return (
    <Page title='Recruitment Job Roles'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading='Job Roles'
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Recruitment', href: PATH_DASHBOARD.recruitment.root }, { name: 'Job Roles' }]}
          action={
            <Button variant='contained' startIcon={<Iconify icon='eva:plus-fill' />} onClick={() => manageModel(dataModel, 'add')}>
              New Job Role
            </Button>
          }
        />

        {accessVerify('GENERAL_DATA') ? (
          <DataGridTable
            name='Recruitment Job Roles'
            data={data || []}
            column={columns}
            isLoading={false}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            enableRowSelection={false}
            enableRowActions={false}
            isRowClickable={true}
            onRowClick={(row) => manageModel(row?.original, 'edit')}
            renderRowActionItems={() => []}
          />
        ) : (
          <PermissionRequired />
        )}

        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <SwipeableDrawer
            anchor='top'
            open={isModelOpen}
            onOpen={() => setIsModelOpen(true)}
            onClose={() => setIsModelOpen(false)}
            PaperProps={{
              sx: {
                width: '90%',
                height: '90%',
                marginLeft: '5%',
                marginTop: '3%',
                borderRadius: '10px',
              },
            }}
          >
            <Box sx={{ p: 3 }}>
              <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 3 }}>
                <Typography variant='h5'>{isAdd ? 'Add Job Role' : 'Edit Job Role'}</Typography>
                <Button color='inherit' startIcon={<Iconify icon='eva:close-fill' />} onClick={() => setIsModelOpen(false)}>
                  Close
                </Button>
              </Stack>

              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Stack spacing={3}>
                    <Box sx={{ p: 3, border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                      <Typography variant='subtitle1' padding='0 0 16px'>
                        Role Information
                      </Typography>
                      <Stack spacing={3}>
                        <RHFTextField name='name' label='Job Role Name' />
                        <RHFTextField name='code' label='Role Code' />
                        <RHFSelect name='departmentId' label='Department'>
                          <option value='' />
                          {(departmentData || []).map((department) => (
                            <option key={department.id} value={department.id}>
                              {department.name}
                            </option>
                          ))}
                        </RHFSelect>
                        <RHFSelect name='employmentType' label='Employment Type'>
                          {EMPLOYMENT_TYPES.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </RHFSelect>
                        <RHFSelect name='status' label='Status'>
                          {STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </RHFSelect>
                      </Stack>
                    </Box>

                    <Box sx={{ p: 3, border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                      <Typography variant='subtitle1' padding='0 0 16px'>
                        Hiring Summary
                      </Typography>
                      <Stack spacing={3}>
                        <RHFTextField name='summary' label='Summary' multiline minRows={3} />
                        <RHFTextField name='responsibilities' label='Responsibilities' multiline minRows={4} />
                        <RHFTextField name='requirements' label='Requirements' multiline minRows={4} />
                      </Stack>
                    </Box>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 3, border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                    <Stack spacing={3}>
                      <Typography variant='subtitle1'>Salary Range</Typography>
                      <RHFTextField name='salaryMin' label='Minimum Salary' type='number' />
                      <RHFTextField name='salaryMax' label='Maximum Salary' type='number' />
                      <LoadingButton type='button' variant='contained' size='large' loading={isSubmitting} fullWidth onClick={handleSubmit(onSubmit)}>
                        {isAdd ? 'Create Job Role' : 'Save Changes'}
                      </LoadingButton>
                    </Stack>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </SwipeableDrawer>
        </FormProvider>
      </Container>
    </Page>
  );
}
